/**
 * Übersetzt den inneren Weltzustand in die Sicht, die nach außen geht.
 *
 * Der Renderer sieht NUR das hier. Wer im Renderer etwas braucht, das hier
 * fehlt, ergänzt es hier — und nicht, indem er sich den Weltzustand greift.
 */

import type {
  AbstiegSicht,
  BeuteSicht,
  EffektSicht,
  GeschossSicht,
  Haltung,
  Schnappschuss,
  SlotSicht,
  SpielerSicht,
  WesenSicht,
} from '@pg/protocol';
import { TAKT_DAUER } from '@pg/protocol';
import type { GegnerWesen, Spieler, Welt } from './typen.js';
import { erfahrungFuerStufe } from './welt.js';
import { ladeInhalte } from '@pg/content';

function haltungVon(w: { tot: boolean; wirktNoch: number; gesch: { x: number; y: number } }, bewegt: boolean): Haltung {
  if (w.tot) return 'sterben';
  if (w.wirktNoch > 0) return 'schlagen';
  return bewegt ? 'gehen' : 'stehen';
}

function gegnerSicht(g: GegnerWesen): WesenSicht {
  const bewegt = Math.abs(g.gesch.x) + Math.abs(g.gesch.y) > 0.01 || g.telegraf <= 0;
  return {
    id: g.id,
    art: 'gegner',
    rang: g.rang,
    // Gewoehnliche Gegner brauchen kein Namensschild — nur Anfuehrer.
    anzeigename: g.rang === 'gewoehnlich' ? '' : g.anzeigename,
    bauplan: g.bauplan,
    pos: { x: g.pos.x, y: g.pos.y },
    winkel: g.winkel,
    radius: g.radius,
    haltung: haltungVon(g, bewegt && g.telegraf <= 0),
    leben: g.leben,
    lebenMax: g.lebenMax,
    benannt: g.rang !== 'gewoehnlich',
    aktion: g.wirktNoch > 0 ? 1 - g.wirktNoch / 0.25 : 0,
    telegraf: Math.max(0, g.telegraf),
  };
}

function slotSicht(s: Spieler): SlotSicht[] {
  return s.slots.map((slot, i) => ({
    slot: i,
    faehigkeit: slot.faehigkeit.id,
    name: slot.faehigkeit.name,
    handbetrieb: slot.handbetrieb,
    automatikfaehig: slot.faehigkeit.automatikfaehig ?? false,
    grundangriff: i === 0 && (slot.faehigkeit.grundangriff ?? false),
    kategorie: slot.faehigkeit.kategorie ?? 'angriff',
    bewegtSpieler: (slot.faehigkeit.eigenbewegung ?? 0) > 0,
    bezahlbar: s.vorrat >= slot.faehigkeit.vorrat,
    abklingen: slot.abklingen,
    abklingenMax: slot.faehigkeit.abklingen,
    regelAktiv: slot.regelAktiv,
    manuell: slot.manuellAngefordert,
  }));
}

function spielerSicht(s: Spieler): SpielerSicht {
  const bewegt = !s.anker && (s.autolauf || Math.abs(s.richtung.x) + Math.abs(s.richtung.y) > 0.08);
  const inhalte = ladeInhalte();
  return {
    id: s.id,
    art: 'spieler',
    rang: 'gewoehnlich',
    anzeigename: '',
    bauplan: s.klasse,
    pos: { x: s.pos.x, y: s.pos.y },
    winkel: s.winkel,
    radius: s.radius,
    haltung: haltungVon(s, bewegt),
    leben: s.leben,
    lebenMax: s.lebenMax,
    benannt: false,
    aktion: s.wirktGesamt > 0 ? 1 - s.wirktNoch / s.wirktGesamt : 0,
    telegraf: 0,
    stufe: s.stufe,
    erfahrung: s.erfahrung,
    erfahrungNaechste: erfahrungFuerStufe(s.stufe),
    vorrat: s.vorrat,
    vorratMax: s.vorratMax,
    gold: s.gold,
    slots: slotSicht(s),
    autolauf: s.autolauf,
    anker: s.anker,
    automatikgrad: s.automatikgrad,
    tempoprofil: '',
    freigeschaltet: [...s.freigeschaltet],
    offeneWahl:
      s.offeneWahl?.map((id) => {
        const f = inhalte.faehigkeiten.get(id);
        return {
          id,
          name: f?.name ?? id,
          beschreibung: f?.beschreibung ?? '',
          bewegtSpieler: (f?.eigenbewegung ?? 0) > 0,
        };
      }) ?? null,
  };
}

/** Gattungen, die im Vorbeigehen genommen werden. Alles andere wird angeklickt. */
const IM_VORBEIGEHEN = new Set(['gold', 'fladen']);

export function baueSchnappschuss(welt: Welt, effekte: readonly EffektSicht[]): Schnappschuss {
  const wesen: WesenSicht[] = [];
  for (const g of welt.gegner) wesen.push(gegnerSicht(g));

  const k = welt.begleiter;
  wesen.push({
    id: k.id,
    art: 'begleiter',
    rang: 'gewoehnlich',
    anzeigename: '',
    bauplan: 'kruemel',
    pos: { x: k.pos.x, y: k.pos.y },
    winkel: k.winkel,
    radius: k.radius,
    haltung: k.zustand === 'unterwegs' ? 'stehen' : 'gehen',
    leben: 1,
    lebenMax: 1,
    benannt: false,
    aktion: k.korb.length / Math.max(1, k.korbMax),
    telegraf: 0,
  });

  const beute: BeuteSicht[] = welt.beute
    .filter((b) => !b.aufgehoben)
    .map((b) => ({
      id: b.id,
      pos: { x: b.pos.x, y: b.pos.y },
      seltenheit: b.seltenheit,
      name: b.name,
      gattung: b.gattung,
      menge: b.menge,
      anklicken: !IM_VORBEIGEHEN.has(b.gattung),
      inReichweite:
        Math.hypot(b.pos.x - welt.spieler.pos.x, b.pos.y - welt.spieler.pos.y) <=
        welt.aufhebeReichweite,
    }));

  const geschosse: GeschossSicht[] = welt.geschosse.map((p) => ({
    id: p.id,
    bauplan: p.bauplan,
    pos: { x: p.pos.x, y: p.pos.y },
    winkel: Math.atan2(p.gesch.y, p.gesch.x),
  }));

  const abstieg: AbstiegSicht = {
    region: welt.region,
    tiefe: welt.tiefe,
    grad: welt.grad,
    stand: welt.stand,
    aufgabe: {
      text: welt.aufgabe.text,
      fortschritt: welt.aufgabe.fortschritt,
      ziel: welt.aufgabe.ziel,
    },
    ziel: { x: welt.karte.ziel.x, y: welt.karte.ziel.y },
    ausgang: welt.ausgang ? { x: welt.ausgang.x, y: welt.ausgang.y } : null,
    ausgangInReichweite: welt.ausgang
      ? Math.hypot(welt.ausgang.x - welt.spieler.pos.x, welt.ausgang.y - welt.spieler.pos.y) <=
        2.6
      : false,
  };

  return {
    takt: welt.takt,
    zeit: welt.takt * TAKT_DAUER,
    spieler: { ...spielerSicht(welt.spieler), tempoprofil: welt.tempoprofil },
    wesen,
    beute,
    geschosse,
    effekte: effekte.slice(),
    abstieg,
  };
}

/**
 * Zahlenfolge, die den Zustand eindeutig kennzeichnet — Grundlage der
 * goldenen Läufe. Bewusst NUR spielrelevante Werte: Kosmetik darf sich
 * ändern, ohne einen goldenen Lauf zu brechen.
 */
export function* signaturWerte(welt: Welt): Generator<number> {
  yield welt.takt;
  yield welt.spieler.pos.x;
  yield welt.spieler.pos.y;
  yield welt.spieler.leben;
  yield welt.spieler.vorrat;
  yield welt.spieler.stufe;
  yield welt.spieler.erfahrung;
  yield welt.spieler.gold;
  yield welt.gegner.length;
  for (const g of welt.gegner) {
    yield g.id;
    yield g.pos.x;
    yield g.pos.y;
    yield g.leben;
  }
  yield welt.beute.length;
  for (const b of welt.beute) {
    yield b.id;
    yield b.menge;
    yield b.gegenstand ? b.gegenstand.affixe.length : 0;
    for (const a of b.gegenstand?.affixe ?? []) yield a.betrag;
  }
}
